<template>
  <div>
    <BaseCottageListPage
      v-if="!pending && cottagesData?.cottages && cottagesMeta?.pagination"
      :cottages-data="cottagesData"
      :cottages-meta="cottagesMeta"
      :location-name="locationName"
    >
      <template #default>
        <CottageListReviews :items="reviewsData" :title="locationName" />
        <CottageListSeoContent v-if="seoData" :seo-data="seoData" />
        <CottageListCloseCottages
          :items="closeCottagesData"
          :title="locationName"
        />
      </template>
    </BaseCottageListPage>
  </div>
</template>

<script setup lang="ts">
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { serverFetch as fetchSeoDestination } from '~~/modules/api/rurality/v3/search/seo/destination'
import { serverFetch as getDestinationModules } from '~~/modules/api/rurality/v3/search/seo/destination-modules'
import { serverFetch as fetchCottageReviews } from '~~/modules/api/rurality/v3/cottage-reviews/destination/detail'
import type { schemaType as reviewsSchemaType } from '~~/modules/api/rurality/v3/cottage-reviews/destination/detail/schema'
import type { schemaType as destinationModulesSchemaType } from '~~/modules/api/rurality/v3/search/seo/destination-modules/schema'
import { useCottageListPage } from '~~/modules/views/cottage-list/composables/useCottageListPage'
import { useMenuBreadcrumbStore } from '~~/modules/menu/stores/useMenuBreadcrumbStore'
import { useSendCottageStats } from '~~/modules/views/cottage-list/composables/useSendCottageStats'
import { COTTAGE_LIST_LIST_ROUTE_NAME } from '~~/modules/views/cottage-list/constants'

definePageMeta({
  name: COTTAGE_LIST_LIST_ROUTE_NAME,
})

const { setHeadInfo } = useHeadInfoComposable()
const breadCrumbMenuStore = useMenuBreadcrumbStore()

const route = useRoute()
const slug = route.params.slug as string
const {
  pending,
  cottagesData,
  cottagesMeta,
  error,
  getCurrentLocationName,
  initializePage,
  getBreadcrumbData,
  getHeadInfoData,
  showErrorPage,
} = useCottageListPage({ slug: slug })

await initializePage()

if (import.meta.client) {
  const { sendCottageStats, sendCottageStatsCloudwatch } = useSendCottageStats()
  sendCottageStats(cottagesData.value)
  sendCottageStatsCloudwatch(cottagesData.value)
}

let seoData
let closeCottagesData: destinationModulesSchemaType['modules'] = []
let reviewsData: reviewsSchemaType['reviews'] = []
let locationName: string

if (error.value) {
  showErrorPage(error.value)
} else {
  locationName = getCurrentLocationName()
  setHeadInfo(getHeadInfoData(true))
  const breadcrumbData = getBreadcrumbData()
  if (breadcrumbData) {
    breadCrumbMenuStore.setMenuBreadcrumb(
      breadcrumbData.name,
      breadcrumbData.middleItems,
    )
  }

  if (cottagesData.value?.destination) {
    const closeCottagesCall = await getDestinationModules({
      destinationId: cottagesData.value.destination.id,
      searchListType: 'list',
      page: route.query.page ? Number(route.query.page) : 1,
    })
    closeCottagesData = closeCottagesCall?.success?.data?.modules || []

    const seoDataCall = await fetchSeoDestination({
      slug: cottagesData.value.destination.slug,
    })
    seoData = seoDataCall.success?.data

    const reviewsDataCall = await fetchCottageReviews({
      destination_slug: cottagesData.value.destination.slug,
      limit: 6,
    })
    reviewsData = reviewsDataCall.success?.data.reviews || []
  }
}
</script>
